import * as React from 'react';
import * as TFF from '@tff/types/tff';
import TFFTypography from '../../../../styled-components/TFFTypography';
import TFFIcon from '../../../../styled-components/TFFIcon';
import FareAttributeLine from './FareAttributeLine';
import { useIntl } from 'react-intl';
import { getNdcBaggageText } from '../../../../util/ancillary-utils';
import { useBookingContext } from '../../../../hooks/use-booking-context';

interface props {
  journey: TFF.Journey;
  icon?: string;
  capitalizedCabinClass: string;
  alignClassToRight?: boolean;
  isFlightDifferent?: boolean;
  selectedOfferAncillaries?: TFF.FlightAlternativesAncillaries;
  ancillaries?: TFF.Ancillaries;
  showAirportsName?: TFF.Airport[];
  labelIcons?: { origin: string; destination: string };
}

const FlightStopsPath: React.FC<props> = ({
  journey,
  icon,
  capitalizedCabinClass,
  alignClassToRight,
  isFlightDifferent,
  selectedOfferAncillaries,
  ancillaries,
  showAirportsName,
  labelIcons,
}) => {
  const segments: TFF.Segment[] = journey.Segments;
  const lastSegment = segments[segments.length - 1];
  const firstSegment: TFF.Segment | undefined = segments[0];
  const { timeFormatter, dateFormatter, weekdayFormatter } = useBookingContext();
  const intl = useIntl();

  const journeyAncillaries = selectedOfferAncillaries?.Journeys[journey.Id];

  const getAncillaryText = (fareAttribute: TFF.FlightAlternativesJourneyAncillary): string => {
    const ancillary = ancillaries?.[fareAttribute.Id];
    const defaultText = fareAttribute.Description;

    if (ancillary) {
      return getNdcBaggageText(ancillary, intl) ?? defaultText;
    }

    return defaultText;
  };

  const departureInfo = {
    label: firstSegment.Origin,
    city: showAirportsName?.find(item => item.id === firstSegment.Origin)?.nameDE,
    hour: timeFormatter.format(new Date(firstSegment.DepartureTime)),
    date: `${weekdayFormatter.format(new Date(firstSegment.DepartureTime))}. ${dateFormatter.format(new Date(firstSegment.DepartureTime))}`,
  };

  const arrivalInfo = {
    label: lastSegment.Destination,
    city: showAirportsName?.find(item => item.id === lastSegment.Destination)?.nameDE,
    hour: timeFormatter.format(new Date(lastSegment.ArrivalTime)),
    date: `${weekdayFormatter.format(new Date(lastSegment.ArrivalTime))}. ${dateFormatter.format(new Date(lastSegment.ArrivalTime))}`,
  };

  const numberOfStops = segments.length - 1;

  return (
    <div className="tff-d-flex" style={alignClassToRight ? { justifyContent: 'space-between' } : undefined}>
      {icon && (
        <span className="tff-journey-icon-container">
          <TFFIcon iconName={icon} />
        </span>
      )}
      <div className="tff-journey-details">
        <div style={labelIcons ? { display: 'flex', alignItems: 'flex-start', gap: '8px' } : undefined}>
          {labelIcons && <TFFIcon iconName={labelIcons.origin} color="color-theme-variant" />}
          <div>
            <TFFTypography text={departureInfo.label} fontWeight="semibold" color="color-theme-variant" />
            {departureInfo.city && <TFFTypography text={departureInfo.city} />}
            <TFFTypography
              text={departureInfo.hour}
              fontWeight="bold"
              color={isFlightDifferent ? 'color-functional-error-600' : 'color-functional-neutral-900'}
            />
            <TFFTypography
              text={departureInfo.date}
              color={isFlightDifferent ? 'color-functional-error-600' : 'color-functional-neutral-900'}
            />
          </div>
        </div>
        <div className="tff-horizontal-line-pos" style={labelIcons ? { marginLeft: '32px' } : undefined}>
          <div className="tff-horizontal-line-container stops">
            <div className="tff-d-flex">
              <hr className="tff-horizontal-line stops" />
              <div className="tff-circle"></div>
              <hr className="tff-horizontal-line stops" />
            </div>
            {numberOfStops > 0 ? (
              <TFFTypography text={`${numberOfStops} ${numberOfStops > 1 ? 'Stops' : 'Stop'}`} size="small" />
            ) : (
              <TFFTypography text="Direct" size="small" />
            )}
          </div>
        </div>
        <div style={labelIcons ? { display: 'flex', alignItems: 'flex-start', gap: '8px' } : undefined}>
          {labelIcons && <TFFIcon iconName={labelIcons.destination} color="color-theme-variant" />}
          <div>
            <TFFTypography text={arrivalInfo.label} fontWeight="semibold" color="color-theme-variant" />
            {arrivalInfo.city && <TFFTypography text={arrivalInfo.city} />}
            <TFFTypography
              text={arrivalInfo.hour}
              fontWeight="bold"
              color={isFlightDifferent ? 'color-functional-error-600' : 'color-functional-neutral-900'}
            />
            <TFFTypography
              text={arrivalInfo.date}
              color={isFlightDifferent ? 'color-functional-error-600' : 'color-functional-neutral-900'}
            />
          </div>
        </div>
      </div>
      <div className="tff-d-flex" style={{ flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
        <div className="tff-cabin-class-container">
          <span className="tff-cabin-class-icon">
            <TFFIcon iconName="seat" />
          </span>
          <TFFTypography text={capitalizedCabinClass} color="color-functional-neutral-900" />
        </div>
        {journeyAncillaries?.map(
          (fareAttribute, index) =>
            !fareAttribute.Id.includes('SKY') &&
            !fareAttribute.Id.includes('LOUNGE_ACCESS') && (
              <FareAttributeLine key={fareAttribute.Id} ancillaryText={getAncillaryText(fareAttribute)} />
            ),
        )}
      </div>
    </div>
  );
};

export default FlightStopsPath;
